var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import ADD_TENANT_SUPPLIER from 'api/mutations/ADD_TENANT_SUPPLIER';
import ARCHIVE_TENANT_SUPPLIER from 'api/mutations/ARCHIVE_TENANT_SUPPLIER';
import REMOVE_TENANT_SUPPLIER from 'api/mutations/REMOVE_TENANT_SUPPLIER';
import RETRIEVE_TENANT_SUPPLIER from 'api/mutations/RETRIEVE_TENANT_SUPPLIER';
import UPDATE_TENANT_SUPPLIER from 'api/mutations/UPDATE_TENANT_SUPPLIER';
import GET_CURRENT_TENANT_SUPPLIERS from 'api/queries/GET_CURRENT_TENANT_SUPPLIERS';
import client from '../api/environment';
const initialState = {
    items: [],
    status: {
        fetch: 'idle',
        add: 'idle',
        update: 'idle',
        remove: 'idle',
        other: 'idle',
    },
    errors: {
        fetch: null,
        add: null,
        update: null,
        remove: null,
        other: null,
    },
    pageInfo: {
        hasNextPage: true,
        endCursor: null,
    },
    filters: {
        name: null,
        statusId: '',
    },
};
export const fetchSuppliers = createAsyncThunk('suppliers/fetchSuppliers', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { getState, rejectWithValue }) {
    const state = getState();
    const { filters, pageInfo } = state.suppliers;
    try {
        const response = yield client.query({
            query: GET_CURRENT_TENANT_SUPPLIERS,
            variables: {
                name: filters.name || null,
                statusId: filters.statusId || null,
                first: 20,
                after: pageInfo.endCursor,
            },
            fetchPolicy: 'network-only',
        });
        return response.data.me.currentTenant.suppliers;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addSupplier = createAsyncThunk('suppliers/addSupplier', (variables_1, _a) => __awaiter(void 0, [variables_1, _a], void 0, function* (variables, { rejectWithValue }) {
    try {
        const response = yield client.mutate({
            mutation: ADD_TENANT_SUPPLIER,
            variables,
        });
        return response.data.addSupplier.supplier;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const updateSupplier = createAsyncThunk('suppliers/updateSupplier', (variables_1, _a) => __awaiter(void 0, [variables_1, _a], void 0, function* (variables, { rejectWithValue }) {
    try {
        const response = yield client.mutate({
            mutation: UPDATE_TENANT_SUPPLIER,
            variables,
        });
        return response.data.updateSupplier.supplier;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const archiveSupplier = createAsyncThunk('suppliers/archiveSupplier', (supplierId_1, _a) => __awaiter(void 0, [supplierId_1, _a], void 0, function* (supplierId, { rejectWithValue }) {
    try {
        const response = yield client.mutate({
            mutation: ARCHIVE_TENANT_SUPPLIER,
            variables: { supplierId },
        });
        return response.data.archiveSupplier.supplier;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const retrieveSupplier = createAsyncThunk('suppliers/retrieveSupplier', (supplierId_1, _a) => __awaiter(void 0, [supplierId_1, _a], void 0, function* (supplierId, { rejectWithValue }) {
    try {
        const response = yield client.mutate({
            mutation: RETRIEVE_TENANT_SUPPLIER,
            variables: { supplierId },
        });
        return response.data.retrieveSupplier.supplier;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const removeSupplier = createAsyncThunk('suppliers/removeSupplier', (supplierId_1, _a) => __awaiter(void 0, [supplierId_1, _a], void 0, function* (supplierId, { rejectWithValue }) {
    try {
        const response = yield client.mutate({
            mutation: REMOVE_TENANT_SUPPLIER,
            variables: { supplierId },
        });
        if (response.data.removeSupplier.removed) {
            return supplierId;
        }
        return rejectWithValue('Remove failed');
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const suppliersSlice = createSlice({
    name: 'suppliers',
    initialState,
    reducers: {
        setFilter(state, action) {
            const { filterName, value } = action.payload;
            state.filters[filterName] = value;
        },
        resetFilters(state) {
            state.filters = {
                name: null,
                statusId: null,
            };
        },
        resetSuppliers(state) {
            state.items = [];
            state.pageInfo = { hasNextPage: true, endCursor: null };
            state.status.fetch = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSuppliers.pending, (state) => {
            state.status.fetch = 'loading';
        })
            .addCase(fetchSuppliers.fulfilled, (state, action) => {
            var _a;
            const newSuppliers = action.payload.edges.map((edge) => edge.node);
            const uniqueSuppliers = newSuppliers.filter((newSupplier) => !state.items.some((existingSupplier) => existingSupplier.id === newSupplier.id));
            state.items = [...state.items, ...uniqueSuppliers];
            state.pageInfo = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.pageInfo;
            state.status.fetch = 'succeeded';
        })
            .addCase(fetchSuppliers.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch = action.error.message || 'Error fetching suppliers';
        })
            .addCase(addSupplier.pending, (state) => {
            state.status.add = 'loading';
            state.errors.add = null;
        })
            .addCase(addSupplier.fulfilled, (state, action) => {
            state.items = [action.payload, ...state.items];
            state.status.add = 'succeeded';
        })
            .addCase(addSupplier.rejected, (state, action) => {
            state.status.add = 'failed';
            state.errors.add = action.payload;
        })
            .addCase(updateSupplier.pending, (state) => {
            state.status.update = 'loading';
            state.errors.update = null;
        })
            .addCase(updateSupplier.fulfilled, (state, action) => {
            const updated = action.payload;
            const itemIndex = state.items.findIndex((item) => item.id === updated.id);
            if (itemIndex !== -1) {
                state.items[itemIndex] = Object.assign(Object.assign({}, state.items[itemIndex]), updated);
            }
            state.status.update = 'succeeded';
        })
            .addCase(updateSupplier.rejected, (state, action) => {
            state.status.update = 'failed';
            state.errors.update = action.payload;
        })
            .addCase(archiveSupplier.pending, (state) => {
            state.status.other = 'loading';
        })
            .addCase(archiveSupplier.fulfilled, (state, action) => {
            const updated = action.payload;
            const itemIndex = state.items.findIndex((item) => item.id === updated.id);
            if (itemIndex !== -1) {
                state.items[itemIndex] = Object.assign(Object.assign({}, state.items[itemIndex]), updated);
            }
            state.status.other = 'succeeded';
        })
            .addCase(archiveSupplier.rejected, (state, action) => {
            state.status.other = 'failed';
            state.errors.other = action.payload;
        })
            .addCase(retrieveSupplier.pending, (state) => {
            state.status.other = 'loading';
        })
            .addCase(retrieveSupplier.fulfilled, (state, action) => {
            const updated = action.payload;
            const itemIndex = state.items.findIndex((item) => item.id === updated.id);
            if (itemIndex !== -1) {
                state.items[itemIndex] = Object.assign(Object.assign({}, state.items[itemIndex]), updated);
            }
            state.status.other = 'succeeded';
        })
            .addCase(retrieveSupplier.rejected, (state, action) => {
            state.status.other = 'failed';
            state.errors.other = action.payload;
        })
            .addCase(removeSupplier.pending, (state) => {
            state.status.remove = 'loading';
        })
            .addCase(removeSupplier.fulfilled, (state, action) => {
            state.items = state.items.filter((item) => item.id !== action.payload);
            state.status.remove = 'succeeded';
        })
            .addCase(removeSupplier.rejected, (state, action) => {
            state.status.remove = 'failed';
            state.errors.remove = action.payload;
        });
    },
});
export const { setFilter, resetFilters, resetSuppliers } = suppliersSlice.actions;
export const selectSuppliers = (state) => state.suppliers;
export const selectSuppliersStatus = (state) => state.suppliers.status;
export const selectSuppliersPageInfo = (state) => state.suppliers.pageInfo;
export default suppliersSlice.reducer;
